/**
 * LiveRoomTelemetryProbe — F4 dark instrumentation + guarded camera recovery.
 *
 * Lives INSIDE <LiveKitRoom> so it can see the actual Room object — the
 * outer useLiveMountTelemetry hook cannot observe track/room lifecycle
 * (the keyed inner room remounts invisibly to it).
 *
 * Emits (local participant only — never identities, tokens or room names):
 *   livekit:reconnecting / reconnected / disconnected / connected
 *   track:published / unpublished / muted / unmuted / ended
 *   camera:acquire-failed / recover-attempt / recovered / recover-failed
 *
 * Foreground camera recovery — STRICT conditions (never auto-enables a
 * camera the member turned off):
 *   1. this client is a publisher (canPublish)
 *   2. localParticipant.isCameraEnabled === true  → member INTENDS cam on
 *   3. the underlying MediaStreamTrack is ended, OR still system-muted
 *      1.5s after returning to foreground (iOS interruption, not user mute)
 */
import { useEffect, useRef } from "react";
import { useRoomContext } from "@livekit/components-react";
import { RoomEvent, Track } from "livekit-client";
import { api } from "@/lib/api";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function LiveRoomTelemetryProbe({ streamId, role, canPublish }) {
  const room = useRoomContext();
  const busyRef = useRef(false);

  useEffect(() => {
    if (!room || !streamId) return;
    let sid = null;
    try { sid = sessionStorage.getItem(`pandax_live_sid_${streamId}`); } catch { /* noop */ }
    if (!sid) sid = `probe-${Math.random().toString(16).slice(2)}${Date.now().toString(16)}`;

    const emit = (event, reason = null, meta = null) => {
      try {
        api.post("/telemetry/live-mount", {
          session_correlation_id: sid,
          event,
          stream_id: streamId,
          role: role || null,
          reason,
          meta: meta || undefined,
        }).catch(() => {});
      } catch { /* never break the room over telemetry */ }
    };

    const isLocal = (participant) => participant === room.localParticipant;
    const pubMeta = (pub) => ({ source: String(pub?.source || ""), kind: String(pub?.kind || "") });

    const onReconnecting = () => emit("livekit:reconnecting", "livekit-reconnect");
    const onReconnected = () => emit("livekit:reconnected", "livekit-reconnect");
    const onDisconnected = () => emit("livekit:disconnected");
    const onConnected = () => emit("livekit:connected");
    const onLocalPublished = (pub) => emit("track:published", null, pubMeta(pub));
    const onLocalUnpublished = (pub) => emit("track:unpublished", null, pubMeta(pub));
    const onMuted = (pub, participant) => { if (isLocal(participant)) emit("track:muted", null, pubMeta(pub)); };
    const onUnmuted = (pub, participant) => { if (isLocal(participant)) emit("track:unmuted", null, pubMeta(pub)); };
    const onTrackEnded = () => emit("track:ended", "track-ended");
    const onMediaError = (e) => emit("camera:acquire-failed", "media-error", { error_name: String(e?.error?.name || e?.name || "unknown") });

    room.on(RoomEvent.Reconnecting, onReconnecting);
    room.on(RoomEvent.Reconnected, onReconnected);
    room.on(RoomEvent.Disconnected, onDisconnected);
    room.on(RoomEvent.Connected, onConnected);
    room.on(RoomEvent.LocalTrackPublished, onLocalPublished);
    room.on(RoomEvent.LocalTrackUnpublished, onLocalUnpublished);
    room.on(RoomEvent.TrackMuted, onMuted);
    room.on(RoomEvent.TrackUnmuted, onUnmuted);
    room.on(RoomEvent.MediaDevicesError, onMediaError);
    room.localParticipant?.on?.("localTrackEnded", onTrackEnded);

    const tryRecoverCamera = async () => {
      if (busyRef.current || !canPublish) return;
      const lp = room.localParticipant;
      if (!lp || lp.isCameraEnabled !== true) return; // member turned cam off — NEVER auto-enable
      const pub = lp.getTrackPublication?.(Track.Source.Camera);
      const track = pub?.track;
      const ms = track?.mediaStreamTrack;
      if (!track || !ms) return;
      let dead = ms.readyState === "ended";
      const sysMuted = ms.muted === true; // system-level (iOS suspension), distinct from user mute
      if (!dead && !sysMuted) return;     // healthy — nothing to do
      if (!dead && sysMuted) {
        await sleep(1500);                // grace — iOS usually resumes on its own
        if (lp.isCameraEnabled !== true) return;
        dead = ms.readyState === "ended";
        if (!dead && ms.muted !== true) return; // self-recovered
      }
      busyRef.current = true;
      const reason = dead ? "track-ended" : "system-mute";
      emit("camera:recover-attempt", reason, { ready_state: String(ms.readyState), sys_muted: String(ms.muted) });
      try {
        await track.restartTrack();
        emit("camera:recovered", reason);
      } catch {
        emit("camera:recover-failed", reason);
      } finally {
        busyRef.current = false;
      }
    };

    const onVis = () => {
      if (document.visibilityState === "visible") tryRecoverCamera();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      document.removeEventListener("visibilitychange", onVis);
      room.off(RoomEvent.Reconnecting, onReconnecting);
      room.off(RoomEvent.Reconnected, onReconnected);
      room.off(RoomEvent.Disconnected, onDisconnected);
      room.off(RoomEvent.Connected, onConnected);
      room.off(RoomEvent.LocalTrackPublished, onLocalPublished);
      room.off(RoomEvent.LocalTrackUnpublished, onLocalUnpublished);
      room.off(RoomEvent.TrackMuted, onMuted);
      room.off(RoomEvent.TrackUnmuted, onUnmuted);
      room.off(RoomEvent.MediaDevicesError, onMediaError);
      room.localParticipant?.off?.("localTrackEnded", onTrackEnded);
    };
  }, [room, streamId, role, canPublish]);

  return null;
}
